import React, { useState, useEffect } from 'react';
import Modal from '@mui/material/Modal';
import { Button } from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import { useEntityScreeningLenses } from 'hooks/useEntityScreeningLenses';

interface DatabaseFilterModalProps {
  open: boolean;
  onClose: () => void;
  statusFilters: string[];
  riskFilters: string[];
  hitCountRanges: string[];
  nameStartsWith: string[];
  hasNewResults: 'yes' | 'no' | null;
  lensFilters: string[];
  onApply: (filters: {
    statuses: string[];
    risks: string[];
    hitCountRanges: string[];
    nameStartsWith: string[];
    hasNewResults: 'yes' | 'no' | null;
    lenses: string[];
  }) => void;
}

const STATUS_OPTIONS = [
  { value: 'new', label: 'New' },
  { value: 'not_reviewed', label: 'Not Reviewed' },
  { value: 'cleared', label: 'Cleared' },
  { value: 'quarantined', label: 'Quarantined' },
  { value: 'barred', label: 'Barred' },
];

const RISK_OPTIONS = [
  { value: 'High', label: 'High' },
  { value: 'Medium', label: 'Medium' },
  { value: 'Low', label: 'Low' },
  { value: 'None', label: 'None' },
];

const NEW_RESULTS_OPTIONS = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
];

const HIT_COUNT_RANGE_OPTIONS = [
  { value: '0-0', label: '0' },
  { value: '1-25', label: '1 - 25' },
  { value: '26-50', label: '26 - 50' },
  { value: '51-100', label: '51 - 100' },
  { value: '101-150', label: '101 - 150' },
  { value: '151-200', label: '151 - 200' },
  { value: '201-250', label: '201 - 250' },
];

const ALPHABET_OPTIONS = Array.from({ length: 26 }, (_, index) => {
  const letter = String.fromCharCode('A'.charCodeAt(0) + index);
  return { value: letter, label: letter };
});

const DatabaseFilterModal: React.FC<DatabaseFilterModalProps> = ({
  open,
  onClose,
  statusFilters,
  riskFilters,
  hitCountRanges,
  nameStartsWith,
  hasNewResults,
  lensFilters,
  onApply,
}) => {
  const { lenses: availableLenses } = useEntityScreeningLenses(open);
  const lensOptions = Array.from(
    new Set([...availableLenses, ...lensFilters]),
  ).map((lens) => ({ value: lens, label: lens }));
  const [selectedStatuses, setSelectedStatuses] =
    useState<string[]>(statusFilters);
  const [selectedRisks, setSelectedRisks] = useState<string[]>(riskFilters);
  const [selectedHitCountRanges, setSelectedHitCountRanges] =
    useState<string[]>(hitCountRanges);
  const [selectedNameStartsWith, setSelectedNameStartsWith] =
    useState<string[]>(nameStartsWith);
  const [selectedNewResults, setSelectedNewResults] = useState<
    'yes' | 'no' | null
  >(hasNewResults);
  const [selectedLenses, setSelectedLenses] = useState<string[]>(lensFilters);

  useEffect(() => {
    if (open) {
      setSelectedStatuses(statusFilters);
      setSelectedRisks(riskFilters);
      setSelectedHitCountRanges(hitCountRanges);
      setSelectedNameStartsWith(nameStartsWith);
      setSelectedNewResults(hasNewResults);
      setSelectedLenses(lensFilters);
    }
  }, [
    open,
    statusFilters,
    riskFilters,
    hitCountRanges,
    nameStartsWith,
    hasNewResults,
    lensFilters,
  ]);

  const handleToggle = (
    value: string,
    setter: React.Dispatch<React.SetStateAction<string[]>>,
  ) => {
    setter((prev) =>
      prev.includes(value)
        ? prev.filter((item) => item !== value)
        : [...prev, value],
    );
  };

  const handleApply = () => {
    onApply({
      statuses: selectedStatuses,
      risks: selectedRisks,
      hitCountRanges: selectedHitCountRanges,
      nameStartsWith: selectedNameStartsWith,
      hasNewResults: selectedNewResults,
      lenses: selectedLenses,
    });
    onClose();
  };

  const handleClear = () => {
    setSelectedStatuses([]);
    setSelectedRisks([]);
    setSelectedHitCountRanges([]);
    setSelectedNameStartsWith([]);
    setSelectedNewResults(null);
    setSelectedLenses([]);
  };

  const handleToggleNewResults = (value: 'yes' | 'no') => {
    setSelectedNewResults((prev) => (prev === value ? null : value));
  };

  const renderOption = (
    option: { value: string; label: string },
    isSelected: boolean,
    onClick: () => void,
  ) => (
    <div
      key={option.value}
      className='bill-container-fit-screen-content-row-checkbox'
      onClick={onClick}
    >
      <div className='bill-container-fit-screen-content-row-input'>
        <div
          className={`bill-container-fit-screen-content-row-input-icon ${
            isSelected ? 'selected' : ''
          }`}
        >
          <div
            className={`bill-container-fit-screen-content-row-input-icon-rnd ${
              isSelected ? 'selected' : ''
            }`}
          />
        </div>
      </div>
      <div className='bill-container-fit-screen-content-row-checkbox-text'>
        <p>{option.label}</p>
      </div>
    </div>
  );

  return (
    <Modal open={open} onClose={onClose}>
      <div
        className='filter-options-container'
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '90%',
          maxWidth: '1200px',
          maxHeight: '90vh',
          overflow: 'hidden',
        }}
      >
        <div className='filter-options-header'>
          <div className='filter-options-header-content'>
            <div className='filter-options-header-icon'>
              <FilterListIcon sx={{ fontSize: 24, color: '#4895EF' }} />
            </div>
            <div className='filter-options-header-text-group'>
              <div className='filter-options-header-text-primary'>
                Database Filters
              </div>
              <div className='filter-options-header-text-secondary'>
                Choose statuses, risk levels, hits, name initials, lenses, and
                new results to narrow the list.
              </div>
            </div>
          </div>
          <div className='filter-options-header-close' onClick={onClose}>
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='24'
              height='24'
              viewBox='0 0 24 24'
              fill='none'
            >
              <path
                d='M18 6L6 18M6 6L18 18'
                stroke='var(--card-text)'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </div>
        </div>

        <div className='filter-options-body-container'>
          <div
            className='filter-column-container'
            style={{ flex: 1, minWidth: 0 }}
          >
            <div className='filter-column-header'>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='19'
                height='18'
                viewBox='0 0 19 18'
                fill='none'
              >
                <path
                  d='M3.75 6.75026V12.7503M7.125 6.75026V12.7503M10.875 6.75026V12.7503M14.25 6.75026V12.7503M2.25 13.9503L2.25 14.5503C2.25 14.9703 2.25 15.1803 2.33175 15.3408C2.40365 15.4819 2.51839 15.5966 2.65951 15.6685C2.81994 15.7503 3.02996 15.7503 3.45 15.7503H14.55C14.97 15.7503 15.1801 15.7503 15.3405 15.6685C15.4816 15.5966 15.5963 15.4819 15.6683 15.3408C15.75 15.1803 15.75 14.9703 15.75 14.5503V13.9503C15.75 13.5302 15.75 13.3202 15.6683 13.1598C15.5963 13.0186 15.4816 12.9039 15.3405 12.832C15.1801 12.7503 14.97 12.7503 14.55 12.7503H3.45C3.02996 12.7503 2.81994 12.7503 2.65951 12.832C2.51839 12.9039 2.40365 13.0186 2.33175 13.1598C2.25 13.3202 2.25 13.5302 2.25 13.9503Z'
                  stroke='#4895EF'
                  strokeWidth='1.5'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
              <div className='filter-column-header-title'>Status</div>
            </div>
            <div
              className='filter-column-options-container'
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                maxHeight: '360px',
                overflowY: 'auto',
              }}
            >
              {STATUS_OPTIONS.map((option) =>
                renderOption(
                  option,
                  selectedStatuses.includes(option.value),
                  () => handleToggle(option.value, setSelectedStatuses),
                ),
              )}
            </div>
          </div>

          <div
            className='filter-column-container'
            style={{ flex: 1, minWidth: 0 }}
          >
            <div className='filter-column-header'>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='19'
                height='18'
                viewBox='0 0 19 18'
                fill='none'
              >
                <path
                  d='M16.5616 11.5058C14.4443 13.0121 11.6671 13.1794 9.40777 12M2.43833 6.49417C4.56079 4.98425 7.34633 4.81978 9.60862 6.00862M12.0303 10.5933C14.1953 9.22876 15.4489 6.7306 15.2007 4.13417M6.93638 7.42778C4.79174 8.79682 3.55219 11.2827 3.79908 13.8658M12.1452 7.60278C12.0454 5.04638 10.5087 2.71263 8.13658 1.62917M6.85671 10.4442C6.97334 12.9822 8.50571 15.294 10.8633 16.3708M14.8033 3.6967C17.7322 6.62563 17.7322 11.3744 14.8033 14.3033C11.8744 17.2322 7.12562 17.2322 4.1967 14.3033C1.26777 11.3744 1.26777 6.62562 4.1967 3.6967C7.12563 0.767767 11.8744 0.767767 14.8033 3.6967ZM11.6213 6.87868C12.7929 8.05025 12.7929 9.94975 11.6213 11.1213C10.4497 12.2929 8.55025 12.2929 7.37868 11.1213C6.20711 9.94975 6.20711 8.05025 7.37868 6.87868C8.55025 5.70711 10.4497 5.70711 11.6213 6.87868Z'
                  stroke='#4895EF'
                  strokeWidth='1.5'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
              <div className='filter-column-header-title'>Risk Level</div>
            </div>
            <div
              className='filter-column-options-container'
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                maxHeight: '360px',
                overflowY: 'auto',
              }}
            >
              {RISK_OPTIONS.map((option) =>
                renderOption(option, selectedRisks.includes(option.value), () =>
                  handleToggle(option.value, setSelectedRisks),
                ),
              )}
            </div>
          </div>

          <div
            className='filter-column-container'
            style={{ flex: 1, minWidth: 0 }}
          >
            <div className='filter-column-header'>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='19'
                height='18'
                viewBox='0 0 19 18'
                fill='none'
              >
                <path
                  d='M2.75 5.25H16.25M4.25 9H14.75M6.25 12.75H12.75'
                  stroke='#4895EF'
                  strokeWidth='1.5'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
              <div className='filter-column-header-title'>Hit Count</div>
            </div>
            <div
              className='filter-column-options-container'
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                maxHeight: '360px',
                overflowY: 'auto',
              }}
            >
              {HIT_COUNT_RANGE_OPTIONS.map((option) =>
                renderOption(
                  option,
                  selectedHitCountRanges.includes(option.value),
                  () => handleToggle(option.value, setSelectedHitCountRanges),
                ),
              )}
            </div>
          </div>

          <div
            className='filter-column-container'
            style={{ flex: 1, minWidth: 0 }}
          >
            <div className='filter-column-header'>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='19'
                height='18'
                viewBox='0 0 19 18'
                fill='none'
              >
                <path
                  d='M9.5 2.25V15.75M3.5 12.75H15.5M4.25 4.5H6.25L9.5 2.25L12.75 4.5H14.75'
                  stroke='#4895EF'
                  strokeWidth='1.5'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
              <div className='filter-column-header-title'>Name Starts With</div>
            </div>
            <div
              className='filter-column-options-container'
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                gap: '12px',
                maxHeight: '360px',
                overflowY: 'auto',
              }}
            >
              {ALPHABET_OPTIONS.map((option) =>
                renderOption(
                  option,
                  selectedNameStartsWith.includes(option.value),
                  () => handleToggle(option.value, setSelectedNameStartsWith),
                ),
              )}
            </div>
          </div>

          <div
            className='filter-column-container'
            style={{ flex: 1, minWidth: 0 }}
          >
            <div className='filter-column-header'>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='19'
                height='18'
                viewBox='0 0 19 18'
                fill='none'
              >
                <path
                  d='M9.5 2.25C5.77208 2.25 2.75 5.27208 2.75 9C2.75 12.7279 5.77208 15.75 9.5 15.75C13.2279 15.75 16.25 12.7279 16.25 9C16.25 5.27208 13.2279 2.25 9.5 2.25ZM9.5 2.25V4.5M9.5 13.5V15.75M4.08934 4.08934L5.68566 5.68566M13.3143 5.68566L14.9107 4.08934M2.25 9H4.5M14.5 9H16.75M4.08934 13.9107L5.68566 12.3143M13.3143 12.3143L14.9107 13.9107'
                  stroke='#4895EF'
                  strokeWidth='1.5'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
              <div className='filter-column-header-title'>Has New Results</div>
            </div>
            <div
              className='filter-column-options-container'
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                maxHeight: '360px',
                overflowY: 'auto',
              }}
            >
              {NEW_RESULTS_OPTIONS.map((option) =>
                renderOption(option, selectedNewResults === option.value, () =>
                  handleToggleNewResults(option.value as 'yes' | 'no'),
                ),
              )}
            </div>
          </div>

          <div
            className='filter-column-container'
            style={{ flex: 1, minWidth: 0 }}
          >
            <div className='filter-column-header'>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='19'
                height='18'
                viewBox='0 0 19 18'
                fill='none'
              >
                <path
                  d='M9.5 2.25C5.77208 2.25 2.75 5.27208 2.75 9C2.75 12.7279 5.77208 15.75 9.5 15.75C13.2279 15.75 16.25 12.7279 16.25 9C16.25 5.27208 13.2279 2.25 9.5 2.25ZM9.5 2.25V4.5M9.5 13.5V15.75M4.08934 4.08934L5.68566 5.68566M13.3143 5.68566L14.9107 4.08934M2.25 9H4.5M14.5 9H16.75M4.08934 13.9107L5.68566 12.3143M13.3143 12.3143L14.9107 13.9107'
                  stroke='#4895EF'
                  strokeWidth='1.5'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
              <div className='filter-column-header-title'>Lenses</div>
            </div>
            <div
              className='filter-column-options-container'
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                maxHeight: '360px',
                overflowY: 'auto',
              }}
            >
              {lensOptions.map((option) =>
                renderOption(
                  option,
                  selectedLenses.includes(option.value),
                  () => handleToggle(option.value, setSelectedLenses),
                ),
              )}
            </div>
          </div>
        </div>

        <div className='filter-options-btns-container'>
          <div className='filter-options-btns-spacer'>
            <Button sx={{ color: 'var(--text-primary)' }} onClick={handleClear}>
              Clear
            </Button>
            <Button
              sx={{ color: 'var(--text-primary)', background: '#4895EF' }}
              onClick={handleApply}
            >
              Apply
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default DatabaseFilterModal;
